import { Component, Inject, OnInit } from '@angular/core';

import { RegistrationService } from 'app/features/register/services/registration.service';
import { ProfileInfo } from 'app/features/register/models/profile-info.model';
import { Router } from '@angular/router';
import { ExperianProofingService } from 'app/features/register/services/experian-proofing.service';
import { QUOTE_STEPS } from 'app/features/register/components/routeGuard/workflow.model';
import { WorkflowService } from 'app/features/register/components/routeGuard/workflow.service';

import { HarpValidators } from '@shared/utils/validators';
import { InputConfig } from '@shared/models/input-config.model';
import { RegexService, RegEx } from '@shared/services/regex.service';
import { SpinnerService } from '@shared/services/spinner.service';

import { generateAlphaNumeric } from '@registration/utils/generate-alpha-numeric';

import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';

import {
  USER_ID_ALREADY_EXISTS_ERROR_MSG,
  ALTERNATIVE_USER_ID_ALREADY_EXISTS_ERROR_MSG,
  UNEXPECTED_ERROR_CREATE_USER_MSG,
  GENERIC_UNEXPECTED_ERROR_MSG,
  PASSWORD_VALIDATORS,
  CONFIRM_PASSWORD_VALIDATORS,
  CHALLENGE_QUESTION_VALIDATORS,
  CHALLENGE_QUESTION_ANSWER_VALIDATORS,
  CHALLENGE_QUESTIONS,
  USER_ID_INPUT_CONFIG,
  PASSWORD_INPUT_CONFIG,
  CONFIRM_PASSWORD_INPUT_CONFIG,
  CHALLENGE_QUESTION_INPUT_CONFIG,
  CHALLENGE_QUESTION_ANSWER_INPUT_CONFIG,
  RECAPTCHA_VALIDATORS,
  USER_ID_ALREADY_EXISTS_STATUS_CODE,
  IS_LOA_3_FLOW_FLAG,
  USER_ID_DOES_NOT_EXIST_STATUS_CODE,
} from './account-info.constants';
import { RECAPTCHA_SETTINGS } from 'ng-recaptcha';
import { Observable } from 'rxjs';
import { UserIdentityVerifyService } from '@registration/services/user-identity-verify.service';

@Component({
  selector: 'app-account-info',
  templateUrl: './account-info.component.html',
  styleUrls: ['./account-info.component.scss'],
})
export class AccountInfoComponent implements OnInit {
  profileInfo: Partial<ProfileInfo>;

  challengeQuestions = CHALLENGE_QUESTIONS;

  accountInfoForm: UntypedFormGroup;
  userIdInputConfig: InputConfig;
  passwordInputConfig: InputConfig;
  confirmPasswordInputConfig: InputConfig;
  challengeQuestionInputConfig: InputConfig;
  challengeQuestionAnswerInputConfig: InputConfig;

  errorMessage: string;
  errorCode: string;

  hdim: string;
  jsc: string;
  isHdimLoaded = false;

  captchaConfig$: Observable<any>;

  constructor(
    public workflowService: WorkflowService,
    private registrationService: RegistrationService,
    private experianProofingService: ExperianProofingService,
    private router: Router,
    private spinnerService: SpinnerService,
    private fb: UntypedFormBuilder,
    private regexService: RegexService,
    private userIdentityVerifyService: UserIdentityVerifyService,
    @Inject(RECAPTCHA_SETTINGS) private captchaConfig: Observable<any>,
  ) {
    this.captchaConfig$ = this.captchaConfig;
  }

  ngOnInit() {
    this.experianProofingService.initiateHdim();
    this.experianProofingService.hdim.subscribe(hdim => {
      this.hdim = hdim;
    });
    this.experianProofingService.hdimLoaded.subscribe(hdimLoaded => {
      this.isHdimLoaded = hdimLoaded;
    });
    this.experianProofingService.jsc.subscribe(jsc => {
      this.jsc = jsc;
    });

    this.profileInfo = this.registrationService.getProfileInfo();
    this.workflowService.validateStep(QUOTE_STEPS.registrationStartPage);
    this.initForm();
  }

  private initForm() {
    const usernameRegex = this.regexService.get(RegEx.USER_ID_REGEX);

    this.accountInfoForm = this.fb.group({
      ...(!this.isLoa3Flow && {
        username: ['', [HarpValidators.usernameCheck(usernameRegex)]],
        password: [''],
        confirmPassword: ['', CONFIRM_PASSWORD_VALIDATORS()],
      }),
      ...(this.showRecaptcha && { recaptcha: ['', RECAPTCHA_VALIDATORS] }),
      question: ['', CHALLENGE_QUESTION_VALIDATORS],
      answer: [''],
    });

    /**
     * NOTE: answer validators are set here since they require a reference to the form.
     * The same goes for the password validators being set below.
     */
    this.accountInfoForm
      .get('answer')
      .setValidators(
        CHALLENGE_QUESTION_ANSWER_VALIDATORS({ form: this.accountInfoForm }),
      );

    if (!this.isLoa3Flow) {
      const pwdControl = this.accountInfoForm.get(
        'password',
      ) as UntypedFormControl;

      const passwordRegex = this.regexService.get(RegEx.PASSWORD_REGEX);

      pwdControl.setValidators(
        PASSWORD_VALIDATORS({
          firstName: this.firstName,
          lastName: this.lastName,
          form: this.accountInfoForm,
          passwordRegex,
        }),
      );

      /**
       * NOTE: confirmPassword validators are reset after the password is
       * updated to reconfigure the mustEqual validator.
       */
      pwdControl.valueChanges.subscribe({
        next: password => {
          const confirmPasswordControl =
            this.accountInfoForm.get('confirmPassword');
          confirmPasswordControl.setValidators(
            CONFIRM_PASSWORD_VALIDATORS(password),
          );

          confirmPasswordControl.updateValueAndValidity();
        },
      });
    }

    /**
     * NOTE: This begins form validation on change rather than on blur
     */
    Object.keys(this.accountInfoForm.controls).forEach(key => {
      const control = this.accountInfoForm.get(key);
      control.valueChanges.subscribe({
        next: () => {
          if (control.dirty) {
            control.markAsTouched();
          }
          this.clearErrorMessage();
        },
      });
    });

    this.userIdInputConfig = {
      form: this.accountInfoForm,
      ...USER_ID_INPUT_CONFIG,
    };

    this.passwordInputConfig = {
      form: this.accountInfoForm,
      ...PASSWORD_INPUT_CONFIG,
    };

    this.confirmPasswordInputConfig = {
      form: this.accountInfoForm,
      ...CONFIRM_PASSWORD_INPUT_CONFIG,
    };

    this.challengeQuestionInputConfig = {
      form: this.accountInfoForm,
      ...CHALLENGE_QUESTION_INPUT_CONFIG,
    };

    this.challengeQuestionAnswerInputConfig = {
      form: this.accountInfoForm,
      ...CHALLENGE_QUESTION_ANSWER_INPUT_CONFIG,
    };

    this.spinnerService.stop();
  }

  proofUser() {
    this.clearErrorMessage();
    this.clearErrorCode();
    this.workflowService.validateStep(QUOTE_STEPS.accountInfoPage);
    this.spinnerService.start('Loading...');

    this.setProfileInfo();
    const profileInfo = this.registrationService.getProfileInfo();

    if (!profileInfo.reproofingFlow) {
      profileInfo.experianReferenceId = generateAlphaNumeric();
    }
    profileInfo.ecp1 = profileInfo.password;
    this.registrationService.setProfileInfo(profileInfo);

    this.userIdentityVerifyService.createOktaUser(profileInfo).subscribe(
      oktaResponse => {
        const payload = { ...oktaResponse, ...profileInfo };

        this.experianProofingService
          .proofUser(payload)
          .then(({ responseStatus, errorMessage, errorCode }: any) => {
            this.handleProofingResponse(
              responseStatus,
              errorMessage,
              errorCode,
            );
          })
          .catch(error => {
            this.handleProofingError(error);
          })
          .then(() => {
            this.spinnerService.stop();
          });
      },
      error => {
        this.handleOktaError(error);
        this.spinnerService.stop();
      },
    );
  }

  private handleProofingResponse(
    responseStatus: string,
    errorMessage: string,
    errorCode: string,
  ) {
    switch (responseStatus) {
      case 'CREATE-USER-SUCCESS':
        this.router.navigate(['/register/confirmation']);
        break;
      case 'CREATE-USER-FAIL':
        this.setErrorMessage(errorMessage || UNEXPECTED_ERROR_CREATE_USER_MSG);
        this.setErrorCode(errorCode);
        break;
      default:
        this.setErrorMessage(GENERIC_UNEXPECTED_ERROR_MSG);
        break;
    }
  }

  private handleProofingError(error: any) {
    this.setErrorMessage(GENERIC_UNEXPECTED_ERROR_MSG);
  }

  private handleOktaError(error: any) {
    if (
      error.errorCode === 'ERR-USER-CREATE-0040' ||
      error.errorCode === 'ERR-USER-CREATE-0039'
    ) {
      this.setErrorMessage(error.errorMessage);
    } else {
      this.setErrorMessage(error.errorSummary || GENERIC_UNEXPECTED_ERROR_MSG);
    }
  }

  performUserIDUniquenessCheck() {
    this.spinnerService.start();
    this.registrationService
      .getUserIdInfoFrmOktaSQA({
        username: this.username,
        challengeQuestion: this.question,
        challengeAnswer: this.answer,
      })
      .subscribe(
        res => this.uniquenessCheckSuccess(res),
        () => this.uniquenessCheckFail(),
      );
  }

  uniquenessCheckSuccess({ errorCode, errorCause, statusCode }: any) {
    if (errorCode) {
      this.spinnerService.stop();
      this.setErrorMessage(errorCause);
      this.setErrorCode(errorCode);
      this.spinnerService.stop();
    } else if (statusCode === USER_ID_DOES_NOT_EXIST_STATUS_CODE) {
      this.proofUser();
    } else {
      this.spinnerService.stop();
      // TODO: these msgs are unusual
      this.setErrorMessage(
        statusCode === USER_ID_ALREADY_EXISTS_STATUS_CODE
          ? USER_ID_ALREADY_EXISTS_ERROR_MSG
          : ALTERNATIVE_USER_ID_ALREADY_EXISTS_ERROR_MSG,
      );
      this.setErrorCode(statusCode);
      this.spinnerService.stop();
    }
  }

  uniquenessCheckFail() {
    this.setErrorMessage(GENERIC_UNEXPECTED_ERROR_MSG);
    this.setErrorCode(USER_ID_ALREADY_EXISTS_STATUS_CODE);
    this.spinnerService.stop();
  }

  onBack() {
    this.spinnerService.stop();
    this.clearErrorMessage();
    this.clearErrorCode();
  }

  private setProfileInfo() {
    this.registrationService.setProfileInfo({
      ...this.profileInfo,
      ...(this.username && { username: this.username }),
      ...(this.password && {
        password: this.password,
        confirmPassword: this.confirmPassword,
        ecp1: this.password,
      }),
      ...(this.profileInfo.personalPhoneNumber && {
        personalPhoneNumber: this.profileInfo.personalPhoneNumber,
      }),
      ...(this.recaptcha && { captchaToken: this.recaptcha }),
      customproperty38: this.question,
      ecp2: this.answer,
      reproofpwd: this.isLoa3Flow ? null : this.profileInfo.reproofpwd,
      userId: this.isLoa3Flow ? null : this.profileInfo.userId,
      hdim: this.hdim,
      jsc: this.jsc,
    });
  }

  private setErrorMessage(msg?) {
    this.errorMessage = msg;
  }

  private clearErrorMessage() {
    this.errorMessage = undefined;
  }

  private setErrorCode(code?) {
    this.errorCode = code;
  }

  private clearErrorCode() {
    this.errorCode = undefined;
  }

  get username(): string {
    return this.accountInfoForm.value?.username ?? '';
  }

  get password(): string {
    return this.accountInfoForm.value?.password ?? '';
  }

  get confirmPassword(): string {
    return this.accountInfoForm.value?.confirmPassword ?? '';
  }

  get recaptcha(): string {
    return this.accountInfoForm.value?.recaptcha ?? '';
  }

  get question(): string {
    return this.accountInfoForm.value?.question ?? '';
  }

  get answer(): string {
    return this.accountInfoForm.value?.answer ?? '';
  }

  get firstName(): string {
    return this.profileInfo.firstname;
  }

  get lastName(): string {
    return this.profileInfo.lastname;
  }

  get isLoa3Flow(): boolean {
    return this.profileInfo.loa3Flow === IS_LOA_3_FLOW_FLAG;
  }

  /**
   * NOTE: The form is considered "complete" if all account information
   * controls are valid, which is every control except the recaptcha one.
   */
  get isFormComplete(): boolean {
    return !Object.keys(this.accountInfoForm?.controls ?? {}).some(key => {
      const control = this.accountInfoForm.controls[key];

      if (key === 'recaptcha') {
        return false;
      }

      return !control.valid;
    });
  }

  get showRecaptcha(): boolean {
    return this.isFormComplete;
  }
}
